import {
	Component,
	Input,
	Output,
	EventEmitter,
	OnInit,
	OnDestroy,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	forwardRef,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Subject, takeUntil } from "rxjs";

// Models
import { SociedadViewModel } from "src/app/_core/models/centros/SociedadViewModel";

// Services
import { MasterService } from "src/app/_core/providers/master.service";
import { NotificationService } from "src/app/_core/providers/notification.service";

@Component({
    selector: "app-sociedad-autocomplete",
    templateUrl: "./sociedad-autocomplete.component.html",
    styleUrls: ["./sociedad-autocomplete.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SociedadAutocompleteComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class SociedadAutocompleteComponent
	implements OnInit, OnDestroy, ControlValueAccessor
{
	private readonly destroy$ = new Subject<void>();

	// Inputs
	@Input() label = "Sociedad";
	@Input() placeholder = "Buscar sociedad por código o descripción";
	@Input() required = false;
	@Input() disabled = false;
	@Input() hint = "Busque por código o descripción de sociedad";

	// Outputs
	@Output() sociedadSelected = new EventEmitter<SociedadViewModel>();

	// State
	sociedades: SociedadViewModel[] = [];
	sociedadId: number | null = null;
	selectedSociedad: SociedadViewModel | null = null;

	// ControlValueAccessor
	private onChange = (_value: number | null): void => {
		// This is intentional
	};
	private onTouched = (): void => {
		// This is intentional
	};

	constructor(
		private readonly masterService: MasterService,
		private readonly notificationService: NotificationService,
		private readonly cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		this.loadSociedades();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	// ControlValueAccessor implementation
	writeValue(value: number | null): void {
		this.sociedadId = value ?? null;
		this.syncSelected();
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: number | null) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.markForCheck();
	}

	/**
	 * Carga la lista de sociedades
	 */
	private loadSociedades(): void {
		this.masterService
			.getSociedades()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (sociedades) => {
					this.sociedades = sociedades;
					this.syncSelected();
					this.cdr.markForCheck();
				},
				error: () => {
					this.notificationService.showError(
						"Error al cargar las sociedades",
					);
				},
			});
	}

	/** Recalcula el objeto seleccionado a partir del id actual. */
	private syncSelected(): void {
		this.selectedSociedad =
			this.sociedadId != null
				? (this.sociedades.find((s) => s.id === this.sociedadId) ??
					null)
				: null;
	}

	/**
	 * Obtiene el texto mostrado para una sociedad
	 */
	displaySociedad = (sociedad: SociedadViewModel | string): string => {
		if (typeof sociedad === "string") {
			return sociedad;
		}
		return sociedad && sociedad.codigo && sociedad.descripcion
			? `${sociedad.codigo} - ${sociedad.descripcion}`
			: "";
	};

	/** Selección desde el autocomplete `.ds`. */
	onValueChange(sociedad: SociedadViewModel | null): void {
		this.sociedadId = sociedad ? sociedad.id : null;
		this.selectedSociedad = sociedad;
		this.onChange(this.sociedadId);
		this.onTouched();
		if (sociedad) {
			this.sociedadSelected.emit(sociedad);
		}
		this.cdr.markForCheck();
	}

	onControlTouched(): void {
		this.onTouched();
	}
}
